import { useEffect, useRef, useState } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';

/** Below this an image is too small to grab a handle on. */
const MIN_WIDTH = 48;

/**
 * Resize handles for an image in the rich-text editor.
 *
 * Width is committed to the document only on mouse-up. Updating on every
 * mousemove would work, but each change is a transaction, so a single drag
 * would leave dozens of separate undo steps.
 */
export function ImageNodeView({ node, updateAttributes, selected, editor }: NodeViewProps) {
  const { src, alt, title, width } = node.attrs as {
    src: string;
    alt?: string;
    title?: string;
    width?: number | null;
  };

  const imgRef = useRef<HTMLImageElement>(null);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const editable = editor.isEditable;

  // The width actually painted: the in-progress drag if there is one, else the
  // stored attribute, else the image's natural size.
  const displayWidth = dragWidth ?? width ?? undefined;

  const startResize = (event: React.MouseEvent, edge: 'left' | 'right') => {
    event.preventDefault();
    event.stopPropagation();

    const img = imgRef.current;
    if (!img) return;

    const startX = event.clientX;
    const startWidth = img.getBoundingClientRect().width;
    // Never let a drag exceed the editor's content width, or the image would
    // overflow its container and get clipped rather than scaled.
    const maxWidth = img.parentElement?.parentElement?.getBoundingClientRect().width ?? Infinity;

    const onMove = (moveEvent: MouseEvent) => {
      // Dragging the left handle outward means moving left, hence the negation.
      const delta = edge === 'right' ? moveEvent.clientX - startX : startX - moveEvent.clientX;
      const next = Math.round(Math.min(Math.max(startWidth + delta, MIN_WIDTH), maxWidth));
      setDragWidth(next);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Read through the setter so the final mousemove is not lost to a stale
      // closure, then commit once.
      setDragWidth((finalWidth) => {
        if (finalWidth !== null) updateAttributes({ width: finalWidth });
        return null;
      });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // A drag that ends outside the window never fires mouseup on the document in
  // some browsers; clean up defensively when the node unmounts mid-drag.
  useEffect(() => () => setDragWidth(null), []);

  const handleStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: 10,
    height: 36,
    borderRadius: 5,
    background: '#415A91',
    border: '2px solid #fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    cursor: 'ew-resize',
  };

  return (
    <NodeViewWrapper
      as="div"
      style={{
        display: 'inline-block',
        position: 'relative',
        lineHeight: 0,
        maxWidth: '100%',
      }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt ?? ''}
        title={title ?? undefined}
        draggable={false}
        style={{
          width: displayWidth ? `${displayWidth}px` : undefined,
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 4,
          outline: selected && editable ? '2px solid #415A91' : 'none',
          outlineOffset: 2,
        }}
      />

      {/* Handles appear only for a selected image in an editable document, so
          they never show in the simulator or a shared flow. */}
      {selected && editable && (
        <>
          <span
            role="presentation"
            aria-hidden
            style={{ ...handleStyle, left: -5 }}
            onMouseDown={(e) => startResize(e, 'left')}
          />
          <span
            role="presentation"
            aria-hidden
            style={{ ...handleStyle, right: -5 }}
            onMouseDown={(e) => startResize(e, 'right')}
          />
          {dragWidth !== null && (
            <span
              style={{
                position: 'absolute',
                bottom: 6,
                right: 6,
                padding: '2px 6px',
                borderRadius: 4,
                background: 'rgba(0,0,0,0.65)',
                color: '#fff',
                fontSize: 11,
                lineHeight: 1.4,
                fontFamily: 'monospace',
              }}
            >
              {dragWidth}px
            </span>
          )}
        </>
      )}
    </NodeViewWrapper>
  );
}
